// A subcontractor's insurances (round 41) — peopleApi's shape one kind over.
//
// The type list mirrors the server's lib/insuranceRecords.ts: the four every
// Australian head contractor asks a subbie's certificate of currency for,
// plus 'other' (with its own label) for contract works, plant cover and the
// like. A fixed set rather than a company-curated one — the names are printed
// on the certificate itself, identical on every certificate in the country.
import { request } from './api';
import type { ExpiryStatus } from './documentsApi';

export type InsuranceType =
  | 'workers_comp'
  | 'public_liability'
  | 'motor_fleet'
  | 'professional_indemnity'
  | 'other';

export const INSURANCE_TYPES: InsuranceType[] = [
  'workers_comp',
  'public_liability',
  'motor_fleet',
  'professional_indemnity',
  'other',
];

export const INSURANCE_TYPE_LABEL: Record<InsuranceType, string> = {
  workers_comp: 'Workers compensation',
  public_liability: 'Public liability',
  motor_fleet: 'Motor fleet',
  professional_indemnity: 'Professional indemnity',
  other: 'Other',
};

export interface InsuranceRecord {
  id: string;
  resourceId: string;
  insuranceType: InsuranceType;
  /** The type's standard label, or an 'other' policy's own — the server
      resolves it so every surface says one thing. */
  title: string;
  otherLabel: string | null;
  insurer: string | null;
  policyNumber: string | null;
  issueDate: string | null;
  /** Never null — the record exists to track it. */
  expiresOn: string;
  /** The fixed 30-day chip, NOT the company's reminder window (spec 03). */
  expiry: ExpiryStatus;
  notes: string | null;
  createdByName: string;
  createdAt: string;
  isSampleData: boolean;
}

export interface InsuranceInput {
  insuranceType: InsuranceType;
  otherLabel?: string | null;
  insurer?: string | null;
  policyNumber?: string | null;
  issueDate?: string | null;
  expiryDate?: string | null;
  notes?: string | null;
}

/**
 * Which kinds hold insurances — the web's one copy of the server's
 * HOLDS_INSURANCES (lib/resourceTypes.ts). Subcontractors, per Nathan's word;
 * suppliers would be one entry here and one there, no migration.
 */
export const HOLDS_INSURANCES: readonly string[] = ['subcontractor'];

export const insurances = {
  list: (resourceId: string) =>
    request<{ records: InsuranceRecord[] }>(`/resources/${resourceId}/insurances`),

  add: (resourceId: string, body: InsuranceInput) =>
    request<{ record: InsuranceRecord }>(`/resources/${resourceId}/insurances`, {
      method: 'POST',
      body,
    }),

  update: (resourceId: string, recordId: string, body: Partial<InsuranceInput>) =>
    request<{ record: InsuranceRecord }>(`/resources/${resourceId}/insurances/${recordId}`, {
      method: 'PATCH',
      body,
    }),

  remove: (resourceId: string, recordId: string) =>
    request<void>(`/resources/${resourceId}/insurances/${recordId}`, { method: 'DELETE' }),
};
